import { getAdminOrgId } from "./admin";
import { extractApiErrorMessage, getApiBase, getAuthHeaders } from "./auth";

const API_BASE = getApiBase();

function defaultHeaders(orgId: string): Record<string, string> {
  return getAuthHeaders({ orgId, json: true });
}

export type EmailTemplateItem = {
  code: string;
  name: string;
  description: string;
};

export type SendEmailResult = {
  sent: number;
  total: number;
  errors: { email: string; error: string }[];
  /** Destinataires sans adresse courriel (affichés en information, aucun envoi) */
  skipped_no_email?: { dossier: string; nom: string; prenom: string }[];
  sent_recipients?: { email: string; prenom?: string; nom?: string; dossier?: string }[];
  /** Doublons ignorés (même courriel plusieurs fois dans la liste) */
  skipped_duplicates?: number;
};

export async function listEmailTemplates(): Promise<{ items: EmailTemplateItem[] }> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/email/templates`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Impossible de charger les templates"));
  }
  return response.json();
}

export async function sendBulkEmail(payload: {
  template_code: string;
  recipients: string[];
  subject: string;
  body_html?: string | null;
  body_text?: string | null;
  from_name?: string | null;
  from_email?: string | null;
  cc?: string[] | null;
  bcc?: string[] | null;
  /** Nom pour la salutation (ex: "Hi John,"). Optionnel. */
  greeting_name?: string | null;
  /** Entente de collaboration (ambassadeur) : nom / orga pour « Bonjour …, » dans le corps du mailto de confirmation. Optionnel. */
  confirmation_name?: string | null;
  /** Texte ajouté après le bouton de confirmation (template DRS). Optionnel. */
  body_extra_after_button?: string | null;
  /** Drone Académie : date et heure de la formation pratique (ex: "25 mars 2026 à 8 h 30"). */
  formation_datetime?: string | null;
  /** Drone Académie : date et heure de la révision en vol (ex: "25 mars 2026 à 14h30"). */
  revision_datetime?: string | null;
  /** Drone Académie : adresse du lieu. */
  venue_address?: string | null;
  /** Drone Académie : date formation (YYYY-MM-DD) pour invitation calendrier ICS. */
  formation_date?: string | null;
  /** Drone Académie : heure formation (HH:MM) pour invitation calendrier ICS. */
  formation_time?: string | null;
  /** Drone Académie : date révision (YYYY-MM-DD) pour invitation calendrier ICS. */
  revision_date?: string | null;
  /** Drone Académie : heure révision (HH:MM) pour invitation calendrier ICS. */
  revision_time?: string | null;
  /** Drone Académie Phase 2 : omit | tbd | scheduled */
  formation_schedule_mode?: string | null;
  revision_schedule_mode?: string | null;
  venue_schedule_mode?: string | null;
  participant_brings_exam_drone?: boolean;
  participant_brings_safety_equipment?: boolean;
  /** Suivi : présentation XLKey (lien prise de rendez-vous, Calendly, etc.). */
  booking_url?: string | null;
  /** Annonce pulvérisation drone CA : lien formulaire de contact (bouton CTA). */
  contact_form_url?: string | null;
  /** Avis Google XLKey : lien « Laisser un avis » (bouton CTA). */
  google_review_url?: string | null;
  /** Suivi uniquement : un seul destinataire ; le lien sondage accepte plusieurs réponses (retransmission). */
  survey_broadcast?: boolean;
  /** Immatriculation drone TC : infos drone optionnelles. */
  purchase_date?: string | null;
  drone_brand?: string | null;
  drone_model?: string | null;
  drone_serial_number?: string | null;
  drone_type?: string | null;
  show_yannick?: boolean;
  show_simon?: boolean;
  show_victor?: boolean;
  /** AGRAS : modèles inclus dans le courriel et la page web. */
  agras_drone_models?: string[] | null;
}): Promise<SendEmailResult> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/email/send`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de l'envoi"));
  }
  return response.json();
}

/** Envoi en masse depuis CSV/Excel (courriel + prénom optionnel), sans ZIP. CC info@xlkey.ca appliqué côté serveur pour l'annonce drone. */
export async function sendBulkEmailFromFile(params: {
  template_code: string;
  subject?: string | null;
  from_name?: string | null;
  from_email?: string | null;
  cc?: string | null;
  cci?: string | null;
  contact_form_url?: string | null;
  show_yannick?: boolean;
  show_simon?: boolean;
  show_victor?: boolean;
  recipients_file: File;
}): Promise<SendEmailResult> {
  const orgId = await getAdminOrgId();
  const form = new FormData();
  form.append("template_code", params.template_code);
  form.append("subject", params.subject?.trim() ?? "");
  if (params.from_name) form.append("from_name", params.from_name);
  if (params.from_email) form.append("from_email", params.from_email);
  if (params.cc?.trim()) form.append("cc", params.cc.trim());
  if (params.cci?.trim()) form.append("cci", params.cci.trim());
  if (params.contact_form_url?.trim()) form.append("contact_form_url", params.contact_form_url.trim());
  if (params.show_yannick !== undefined) form.append("show_yannick", String(params.show_yannick));
  if (params.show_simon !== undefined) form.append("show_simon", String(params.show_simon));
  if (params.show_victor !== undefined) form.append("show_victor", String(params.show_victor));
  form.append("recipients_file", params.recipients_file);
  const response = await fetch(`${API_BASE}/email/send-bulk-from-file`, {
    method: "POST",
    headers: getAuthHeaders({ orgId, json: false }),
    body: form,
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de l'envoi en masse"));
  }
  return response.json();
}

function filenameFromContentDisposition(value: string | null): string | null {
  if (!value) return null;
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
  const match = value.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? null;
}

export async function exportAmbassadeurLetterPdf(payload: {
  greeting_name?: string | null;
  confirmation_name?: string | null;
  subject?: string | null;
}): Promise<{ blob: Blob; filename: string }> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/email/ambassadeur-lettre-engagement-01/pdf`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Impossible d'exporter le PDF"));
  }
  const blob = await response.blob();
  return {
    blob,
    filename:
      filenameFromContentDisposition(response.headers.get("Content-Disposition")) ??
      "XLKey-entente-collaboration-ambassadeur.pdf",
  };
}

/** Envoi liste (onglet Liste) avec pièces jointes optionnelles. Même champs que send, plus fichiers. */
export async function sendBulkEmailListWithAttachments(params: {
  template_code: string;
  subject: string;
  from_name?: string | null;
  from_email?: string | null;
  greeting_name?: string | null;
  confirmation_name?: string | null;
  body_extra_after_button?: string | null;
  recipients: string;
  cc?: string | null;
  bcc?: string | null;
  files?: File[];
  formation_datetime?: string | null;
  revision_datetime?: string | null;
  venue_address?: string | null;
  formation_date?: string | null;
  formation_time?: string | null;
  revision_date?: string | null;
  revision_time?: string | null;
  formation_schedule_mode?: string | null;
  revision_schedule_mode?: string | null;
  venue_schedule_mode?: string | null;
  participant_brings_exam_drone?: boolean;
  participant_brings_safety_equipment?: boolean;
  booking_url?: string | null;
  contact_form_url?: string | null;
  google_review_url?: string | null;
  survey_broadcast?: boolean;
  purchase_date?: string | null;
  drone_brand?: string | null;
  drone_model?: string | null;
  drone_serial_number?: string | null;
  drone_type?: string | null;
  show_yannick?: boolean;
  show_simon?: boolean;
  show_victor?: boolean;
  agras_drone_models?: string[] | null;
}): Promise<SendEmailResult> {
  const orgId = await getAdminOrgId();
  const form = new FormData();
  form.append("template_code", params.template_code);
  form.append("subject", params.subject);
  if (params.from_name) form.append("from_name", params.from_name);
  if (params.from_email) form.append("from_email", params.from_email);
  if (params.greeting_name) form.append("greeting_name", params.greeting_name);
  if (params.confirmation_name?.trim()) form.append("confirmation_name", params.confirmation_name.trim());
  if (params.body_extra_after_button) form.append("body_extra_after_button", params.body_extra_after_button);
  if (params.formation_datetime?.trim()) form.append("formation_datetime", params.formation_datetime.trim());
  if (params.revision_datetime?.trim()) form.append("revision_datetime", params.revision_datetime.trim());
  if (params.venue_address?.trim()) form.append("venue_address", params.venue_address.trim());
  if (params.formation_date?.trim()) form.append("formation_date", params.formation_date.trim());
  if (params.formation_time?.trim()) form.append("formation_time", params.formation_time.trim());
  if (params.revision_date?.trim()) form.append("revision_date", params.revision_date.trim());
  if (params.revision_time?.trim()) form.append("revision_time", params.revision_time.trim());
  if (params.formation_schedule_mode?.trim()) {
    form.append("formation_schedule_mode", params.formation_schedule_mode.trim());
  }
  if (params.revision_schedule_mode?.trim()) {
    form.append("revision_schedule_mode", params.revision_schedule_mode.trim());
  }
  if (params.venue_schedule_mode?.trim()) form.append("venue_schedule_mode", params.venue_schedule_mode.trim());
  if (params.participant_brings_exam_drone) {
    form.append("participant_brings_exam_drone", "true");
  }
  if (params.participant_brings_safety_equipment) {
    form.append("participant_brings_safety_equipment", "true");
  }
  if (params.booking_url?.trim()) form.append("booking_url", params.booking_url.trim());
  if (params.contact_form_url?.trim()) form.append("contact_form_url", params.contact_form_url.trim());
  if (params.google_review_url?.trim()) form.append("google_review_url", params.google_review_url.trim());
  if (params.survey_broadcast) form.append("survey_broadcast", "true");
  if (params.purchase_date?.trim()) form.append("purchase_date", params.purchase_date.trim());
  if (params.drone_brand?.trim()) form.append("drone_brand", params.drone_brand.trim());
  if (params.drone_model?.trim()) form.append("drone_model", params.drone_model.trim());
  if (params.drone_serial_number?.trim()) form.append("drone_serial_number", params.drone_serial_number.trim());
  if (params.drone_type?.trim()) form.append("drone_type", params.drone_type.trim());
  if (params.show_yannick !== undefined) form.append("show_yannick", String(params.show_yannick));
  if (params.show_simon !== undefined) form.append("show_simon", String(params.show_simon));
  if (params.show_victor !== undefined) form.append("show_victor", String(params.show_victor));
  if (params.agras_drone_models?.length) form.append("agras_drone_models", params.agras_drone_models.join(","));
  form.append("recipients", params.recipients);
  if (params.cc) form.append("cc", params.cc);
  if (params.bcc) form.append("bcc", params.bcc);
  (params.files || []).forEach((f) => form.append("files", f));
  const response = await fetch(`${API_BASE}/email/send-list-with-attachments`, {
    method: "POST",
    headers: getAuthHeaders({ orgId, json: false }),
    body: form,
  });
  if (!response.ok) {
    const msg = await extractApiErrorMessage(response, "Échec de l'envoi");
    const err = new Error(msg) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export type SurveyPresentationResponseItem = {
  id: string;
  /** Présent si réponse issue d’un lien partageable (plusieurs soumissions par jeton). */
  survey_token_id?: string | null;
  to_email: string;
  greeting_snapshot: string | null;
  submitted_at: string | null;
  created_at: string | null;
  answers: Record<string, unknown>;
  matched_user: { email: string; display_name: string | null } | null;
  broadcast?: boolean;
};

export async function listSurveyPresentationResponses(params?: {
  limit?: number;
  offset?: number;
}): Promise<{ items: SurveyPresentationResponseItem[]; total: number }> {
  const orgId = await getAdminOrgId();
  const search = new URLSearchParams();
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.offset != null) search.set("offset", String(params.offset));
  const qs = search.toString() ? `?${search.toString()}` : "";
  const response = await fetch(`${API_BASE}/email/survey-presentation-xlkey/responses${qs}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Impossible de charger les réponses au sondage"));
  }
  return response.json();
}

/** Envoi avec liste destinataires (CSV ou Excel: dossier, courriel, nom, prénom) + ZIP. CC/CCI : adresses séparées par espace, virgule ou point-virgule. */
export async function sendBulkEmailWithFiles(payload: {
  template_code: string;
  subject: string;
  from_email?: string | null;
  from_name?: string | null;
  cc?: string | null;
  cci?: string | null;
  recipients_file: File;
  zip_file: File;
  formation_datetime?: string | null;
  revision_datetime?: string | null;
  venue_address?: string | null;
  formation_date?: string | null;
  formation_time?: string | null;
  revision_date?: string | null;
  revision_time?: string | null;
  formation_schedule_mode?: string | null;
  revision_schedule_mode?: string | null;
  venue_schedule_mode?: string | null;
  confirmation_name?: string | null;
}): Promise<SendEmailResult> {
  const orgId = await getAdminOrgId();
  const headers = getAuthHeaders({ orgId, json: false });
  const formData = new FormData();
  formData.append("template_code", payload.template_code);
  formData.append("subject", payload.subject);
  if (payload.from_email) formData.append("from_email", payload.from_email);
  if (payload.from_name) formData.append("from_name", payload.from_name);
  if (payload.cc?.trim()) formData.append("cc", payload.cc.trim());
  if (payload.cci?.trim()) formData.append("cci", payload.cci.trim());
  if (payload.confirmation_name?.trim()) formData.append("confirmation_name", payload.confirmation_name.trim());
  if (payload.formation_datetime?.trim()) formData.append("formation_datetime", payload.formation_datetime.trim());
  if (payload.revision_datetime?.trim()) formData.append("revision_datetime", payload.revision_datetime.trim());
  if (payload.venue_address?.trim()) formData.append("venue_address", payload.venue_address.trim());
  if (payload.formation_date?.trim()) formData.append("formation_date", payload.formation_date.trim());
  if (payload.formation_time?.trim()) formData.append("formation_time", payload.formation_time.trim());
  if (payload.revision_date?.trim()) formData.append("revision_date", payload.revision_date.trim());
  if (payload.revision_time?.trim()) formData.append("revision_time", payload.revision_time.trim());
  if (payload.formation_schedule_mode?.trim()) {
    formData.append("formation_schedule_mode", payload.formation_schedule_mode.trim());
  }
  if (payload.revision_schedule_mode?.trim()) {
    formData.append("revision_schedule_mode", payload.revision_schedule_mode.trim());
  }
  if (payload.venue_schedule_mode?.trim()) {
    formData.append("venue_schedule_mode", payload.venue_schedule_mode.trim());
  }
  formData.append("recipients_file", payload.recipients_file);
  formData.append("zip_file", payload.zip_file);
  const response = await fetch(`${API_BASE}/email/send-with-files`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Échec de l'envoi avec pièces jointes"));
  }
  return response.json();
}

export type EmailSendLogItem = {
  id: string;
  sent_at: string | null;
  to_email: string;
  subject: string;
  template_code: string | null;
  from_email: string | null;
  from_name: string | null;
  status: "sent" | "delivered" | "opened" | "replied" | "failed";
  error_message: string | null;
  /** Nombre de fois que le courriel a été ouvert (pixel de suivi) */
  open_count?: number;
  /** Première ouverture */
  first_opened_at?: string | null;
  /** Dernière ouverture */
  last_opened_at?: string | null;
  /** Durée estimée entre première et dernière ouverture (secondes), si open_count >= 2 */
  read_duration_seconds?: number | null;
};

export type EmailRegisterResponse = {
  items: EmailSendLogItem[];
  total: number;
};

export async function listEmailRegister(params?: {
  limit?: number;
  offset?: number;
  to_email?: string;
  date_from?: string;
  date_to?: string;
}): Promise<EmailRegisterResponse> {
  const orgId = await getAdminOrgId();
  const search = new URLSearchParams();
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.offset != null) search.set("offset", String(params.offset));
  if (params?.to_email?.trim()) search.set("to_email", params.to_email.trim());
  if (params?.date_from?.trim()) search.set("date_from", params.date_from.trim().slice(0, 10));
  if (params?.date_to?.trim()) search.set("date_to", params.date_to.trim().slice(0, 10));
  const qs = search.toString() ? `?${search.toString()}` : "";
  const response = await fetch(`${API_BASE}/email/register${qs}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Impossible de charger le registre"));
  }
  return response.json();
}
